import React from 'react';
import {createFragmentContainer, graphql} from 'react-relay';

import type {TimeRegistrationMutationInput} from './logic/TimeRegistrationModalMutation';
import {createTimeRegistration} from './logic/TimeRegistrationModalMutation';
import TimeRegistrationModalQueryWrapper from './query-wrappers/TimeRegistrationModalQueryWrapper';

import type {TimeRegistrationEntityId} from './types/TimeRegistrationEntityId';
import type {TimeRegistrationModalInfo} from './types/TimeRegistrationModalInfo';
import {toEntityId} from './util/TimeRegistrationModalUtil';
import {TimeRegistrationModalCreate_viewer} from './__generated__/TimeRegistrationModalCreate_viewer.graphql';
import {IntlShape, useIntl} from 'react-intl';
import {TimeRegistrationInitialState} from './types/TimeRegistrationInitialState';
import {hasPermission} from '../../../../util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../../Permissions';

const getInfoText = (formatMessage: IntlShape['formatMessage'], personName?: string | null): TimeRegistrationModalInfo => ({
	headerText: personName
		? formatMessage({id: 'timesheets.modal.create_for_person'}, {personName: personName})
		: formatMessage({id: 'timesheets.modal.create'}),
	creativeButtonText: formatMessage({id: 'timesheets.modal.save_entry'}),
	secondaryButtonText: formatMessage({id: 'timesheets.modal.save_create'}),
});

type CreateModalTimeRegistrationInput = {
	timeRegistrationMutationInput: TimeRegistrationMutationInput;
};

function createModalTimeRegistration({timeRegistrationMutationInput}: CreateModalTimeRegistrationInput): void {
	createTimeRegistration(timeRegistrationMutationInput);
}

export type TimeRegistrationModalCreateProps = {
	viewer: TimeRegistrationModalCreate_viewer;
	personId: string;
	entityId: TimeRegistrationEntityId | null;
	closeModal: () => void;
	showAsModal?: boolean;
	fromTaskModal?: boolean;
	onCreateSuccessCallbacks?: (() => void)[];
	showCloseButton?: boolean;
	initialState?: TimeRegistrationInitialState;
	personLabel?: string;
};

function TimeRegistrationModalCreate({
	viewer,
	personId,
	entityId,
	closeModal,
	showAsModal,
	fromTaskModal,
	onCreateSuccessCallbacks,
	showCloseButton,
	initialState,
	personLabel,
}: TimeRegistrationModalCreateProps): JSX.Element {
	const {formatMessage} = useIntl();
	const personName = personLabel ?? viewer.company?.person?.fullName;
	return (
		<TimeRegistrationModalQueryWrapper
			personId={personId}
			entityId={
				entityId ||
				toEntityId({
					taskId: viewer.task ? viewer.task.id : null,
					projectId: viewer.project ? viewer.project.id : null,
					idleTimeId: null,
				})
			}
			projectId={undefined}
			infoText={getInfoText(
				formatMessage,
				hasPermission(PERMISSION_TYPE.TIME_REGISTRATION_CREATE_ALL) ? personName : undefined
			)}
			shouldValidateEntity
			callbackPositive={timeRegistrationMutationInput => {
				const input = {...timeRegistrationMutationInput};
				if (onCreateSuccessCallbacks) {
					// Merge the two callback lists
					input.onCreateSuccessCallbacks = onCreateSuccessCallbacks.concat(
						input.onCreateSuccessCallbacks ? input.onCreateSuccessCallbacks : []
					);
				}
				createModalTimeRegistration({
					timeRegistrationMutationInput: input,
				});
			}}
			closeModal={closeModal}
			showAsModal={showAsModal}
			fromTaskModal={fromTaskModal}
			showCloseButton={showCloseButton}
			initialState={initialState}
		/>
	);
}

export const TimeRegistrationModalCreateQuery = graphql`
	query TimeRegistrationModalCreate_Query($taskId: ID, $companyTaskId: String, $companyProjectId: String, $personId: ID) {
		viewer {
			actualPersonId
			component(name: "time_registration_modal_create")
			...TimeRegistrationModalCreate_viewer
				@arguments(
					taskId: $taskId
					companyTaskId: $companyTaskId
					companyProjectId: $companyProjectId
					personId: $personId
				)
		}
	}
`;

export default createFragmentContainer(TimeRegistrationModalCreate, {
	viewer: graphql`
		fragment TimeRegistrationModalCreate_viewer on Viewer
		@argumentDefinitions(
			taskId: {type: "ID"}
			companyTaskId: {type: "String!"}
			companyProjectId: {type: "String!"}
			personId: {type: "ID"}
		) {
			actualPersonId
			task(id: $taskId, companyTaskId: $companyTaskId) {
				id
				project {
					id
				}
			}
			project(id: $companyProjectId) {
				id
			}
			company {
				person(id: $personId) {
					fullName
				}
			}
		}
	`,
});
